import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { AutoQuoteShowcase, Shot } from "@/components/auto-quote-showcase";
import { QuoteExampleScroller } from "@/components/quote-example-scroller";
import { ArrowRight, Camera, CalendarDays, CreditCard, FileText, Layers, PencilLine, Plus, PoundSterling, SlidersHorizontal, Stamp } from "lucide-react";
import "./auto-quote.css";

const PAGE_URL = "https://thepropertool.com/auto-quote";
const PRICING_URL = `${import.meta.env.BASE_URL}#pricing`;
const PAGE_TITLE = "Auto Quote | Quotes from Photos, Drawings or a Description | The Proper Tool";
const PAGE_DESCRIPTION = "Create professional quotes in minutes, not hours. Upload photos or drawings, or describe the job. Edit every price and send a branded PDF. 14 day free trial, no card.";

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: PAGE_TITLE,
  description: PAGE_DESCRIPTION,
  url: PAGE_URL,
  publisher: { "@type": "Organization", name: "The Proper Tool", url: "https://thepropertool.com" },
};

const SOFTWARE_APP_SCHEMA = { ...SOFTWARE_SCHEMA, name: "The Proper Tool | Auto Quote", description: PAGE_DESCRIPTION, url: PAGE_URL };

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://thepropertool.com/" },
    { "@type": "ListItem", position: 2, name: "Smart Quoting Software", item: "https://thepropertool.com/ai-quoting-software" },
    { "@type": "ListItem", position: 3, name: "Auto Quote", item: PAGE_URL },
  ],
};

export const FAQS = [
  { q: "Do I need drawings, photos and a description?", a: "No. Any one is enough to start a quote. Add more if you have it, for example a description of finishes alongside photos." },
  { q: "Can I change the prices?", a: "Yes. Every quantity and rate is editable. Lines are priced from a UK construction cost library, and you can override any of them with your own rates." },
  { q: "What does my customer receive?", a: "A professional PDF quote with your logo, company details, project details, totals and itemised work." },
  { q: "How accurate is it?", a: "It depends on the information you give. Clear photos, drawings or a detailed description help. Always check scope, quantities and rates before sending." },
  { q: "What is included in the free trial?", a: "14 days with 5 quote creations in total, shared across Auto, Template and Manual quotes. No credit card required." },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const INPUTS = [
  { label: "Upload photos", icon: Camera },
  { label: "Upload drawings", icon: FileText },
  { label: "Describe the job", icon: PencilLine },
];

const TRIAL = [
  { label: "14 days free", icon: CalendarDays },
  { label: "5 quotes included", icon: FileText },
  { label: "No card needed", icon: CreditCard },
];

const BENEFITS = [
  { t: "Every trade, itemised", d: "From prep and demolition to finishes, each line listed separately.", icon: Layers },
  { t: "UK rates, your call", d: "Priced from a UK cost library. Override any rate with your own.", icon: PoundSterling },
  { t: "Edit before it goes", d: "Change quantities, add lines, remove what does not apply.", icon: SlidersHorizontal },
  { t: "Your brand on the PDF", d: "Logo, company details and totals on a clean document.", icon: Stamp },
];

const RELATED_PAGES = [
  { href: "/quote-from-photo/", label: "Quote from Photo" },
  { href: "/quote-from-drawings/", label: "Quote from Drawings" },
  { href: "/quote-from-description/", label: "Quote from a Description" },
  { href: "/ai-quoting-software/", label: "Smart Quoting Software" },
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/free-trial/", label: "Free Trial" },
];

function TrialBox({ testId }: { testId: string }) {
  return (
    <ul aria-label="Free trial benefits" data-testid={testId} className="inline-flex max-w-full flex-wrap gap-x-5 gap-y-2 list-none m-0 rounded-xl border border-[hsl(var(--aq-teal)/0.3)] bg-[hsl(var(--aq-tint))] px-4 py-3">
      {TRIAL.map(({ label, icon: Icon }) => (
        <li key={label} className="inline-flex items-center gap-2 text-sm font-semibold text-[hsl(var(--aq-teal-deep))]">
          <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {label}
        </li>
      ))}
    </ul>
  );
}

function PrimaryCta({ testId, label = "Start free trial" }: { testId: string; label?: string }) {
  return (
    <Link href="/signup" className="aq-btn aq-btn-primary w-full sm:w-auto" data-testid={testId}>
      {label}
      <ArrowRight className="h-5 w-5" aria-hidden="true" />
    </Link>
  );
}

export default function AutoQuotePage() {
  return (
    <div className="aq-page min-h-[100dvh] pb-20 md:pb-0">
      <SEOHead title={PAGE_TITLE} description={PAGE_DESCRIPTION} canonical="/auto-quote" schema={[WEBPAGE_SCHEMA, SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA]} />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800&display=swap" />
      </Helmet>

      <header className="sticky top-0 z-40 border-b border-[hsl(var(--aq-line))] bg-[hsl(var(--aq-paper)/0.92)] backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-4">
          <Link href="/" className="flex items-center gap-2 shrink-0" data-testid="link-home" aria-label="The Proper Tool home">
            <img src="/logo.png" alt="The Proper Tool" width={120} height={32} className="h-8 w-auto" />
          </Link>
          <nav aria-label="Page" className="ml-auto flex items-center gap-4 text-sm font-semibold">
            <a href="#how-it-works" className="hidden sm:inline hover:text-[hsl(var(--aq-teal-deep))]" data-testid="link-nav-how">How it works</a>
            <a href="#pdf-examples" className="hidden sm:inline hover:text-[hsl(var(--aq-teal-deep))]" data-testid="link-nav-pdf">PDF examples</a>
            <a href={PRICING_URL} className="hover:text-[hsl(var(--aq-teal-deep))]" data-testid="link-nav-pricing">Pricing</a>
            <Link href="/signup" className="rounded-lg bg-[hsl(var(--aq-teal))] px-3 py-2 text-[hsl(var(--aq-paper))] hover:bg-[hsl(var(--aq-teal-deep))]" data-testid="link-nav-signup">Start free</Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="pt-8 pb-10 md:pt-14 md:pb-16" data-testid="section-hero">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 items-center">
            <div className="aq-rise min-w-0">
              <p className="aq-eyebrow">Auto Quote for UK builders</p>
              <h1 className="aq-display text-[2.75rem] sm:text-6xl lg:text-7xl">
                Create professional quotes in <span className="text-[hsl(var(--aq-teal))]">minutes</span>, not hours
              </h1>
              <p className="mt-5 text-lg text-[hsl(var(--aq-muted))] max-w-lg">
                Upload photos or drawings, or describe the job. Any one is enough.
              </p>
              <ul aria-label="Ways to start a quote" className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 list-none p-0">
                {INPUTS.map(({ label, icon: Icon }) => (
                  <li key={label} className="inline-flex items-center gap-2 text-sm font-semibold">
                    <span className="inline-flex items-center gap-2 text-[hsl(var(--aq-navy))]">
                      <Icon className="h-5 w-5 text-[hsl(var(--aq-teal))]" aria-hidden="true" />
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-base font-medium">You get an editable, fully priced quote and a branded PDF.</p>
              <div className="mt-6"><TrialBox testId="list-trial-hero" /></div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <PrimaryCta testId="button-hero-signup" />
                <a href="#how-it-works" className="aq-btn aq-btn-ghost w-full sm:w-auto" data-testid="link-hero-how">See how it works</a>
              </div>
            </div>
            <div className="aq-rise aq-rise-2 min-w-0">
              <Shot testId="img-hero-completed" eager src="/campaign/auto-quote-completed.webp" w={895} h={867} sizes="(min-width: 768px) 48vw, 100vw"
                alt="Auto Quote result showing priced sections such as Preparation, Demolition and Substructure"
                caption="The real result screen. Independent example." />
            </div>
          </div>
        </section>

        <QuoteExampleScroller />

        <AutoQuoteShowcase />

        <section id="pdf-examples" className="aq-section scroll-mt-20 bg-[hsl(var(--aq-navy))] text-[hsl(var(--aq-paper))]" data-testid="section-pdf" aria-labelledby="pdf-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <p className="aq-eyebrow !text-[hsl(184_70%_72%)]">What your customer gets</p>
            <h2 id="pdf-heading" className="aq-h2 !text-[hsl(var(--aq-paper))]">A PDF that wins the job</h2>
            <p className="mt-3 text-[hsl(190_30%_96%/0.75)] max-w-xl">Real pages from two separate jobs.</p>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 items-start [&_figcaption]:!text-[hsl(190_30%_96%/0.7)]" data-testid="pdf-examples">
              <Shot testId="img-pdf-summary" ratio="aspect-[1191/1684]" src="/campaign/auto-quote-pdf-summary.webp" w={1191} h={1684} sizes="(min-width: 640px) 45vw, 100vw"
                alt="Garage conversion PDF quote with company logo, project details, quote summary and priced scope of works"
                caption="Branded summary page with totals." />
              <Shot testId="img-pdf" ratio="aspect-[1191/1684]" src="/campaign/auto-quote-pdf-excerpt.webp" w={1191} h={1684} sizes="(min-width: 640px) 45vw, 100vw"
                alt="One page of an Auto Quote PDF, marked Page 2 of 5, showing remedial work items"
                caption="Itemised work page from a separate job." />
            </div>
          </div>
        </section>

        <section className="aq-section" data-testid="section-benefits" aria-labelledby="benefits-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-10 items-start">
            <div>
              <p className="aq-eyebrow">Your evening back</p>
              <h2 id="benefits-heading" className="aq-h2">The pricing is done. You stay in charge.</h2>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-6 list-none p-0">
              {BENEFITS.map(({ t, d, icon: Icon }) => (
                <li key={t} className="border-t-2 border-[hsl(var(--aq-teal))] pt-4">
                  <Icon className="h-6 w-6 text-[hsl(var(--aq-teal))]" aria-hidden="true" />
                  <h3 className="mt-2 font-bold text-lg">{t}</h3>
                  <p className="text-sm text-[hsl(var(--aq-muted))] mt-1">{d}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="aq-section bg-[hsl(var(--aq-tint))]" data-testid="section-inputs" aria-labelledby="inputs-heading">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
            <div className="min-w-0">
              <p className="aq-eyebrow">Drawings welcome</p>
              <h2 id="inputs-heading" className="aq-h2">Got plans? Upload them.</h2>
              <p className="mt-4 text-[hsl(var(--aq-muted))]">Plans add dimensions and structure. No plans? Photos or a few lines describing the job still work.</p>
              <ul className="mt-6 space-y-2 list-none p-0 text-sm font-medium">
                {["Architect or structural drawings as PDF", "Site photos from your phone", "A plain description of the work"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><Plus className="h-4 w-4 text-[hsl(var(--aq-teal))]" aria-hidden="true" />{t}</li>
                ))}
              </ul>
            </div>
            <Shot testId="img-sample-drawings" ratio="aspect-[4/5]" src="/campaign/sample-drawings.webp" w={800} h={1000} sizes="(min-width: 768px) 45vw, 100vw"
              alt="Sample architectural drawings of the kind you can upload"
              caption="Sample drawings for illustration. Not the source of the quotes shown above." />
          </div>
        </section>

        <section className="aq-section" data-testid="section-pricing" aria-labelledby="pricing-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 id="pricing-heading" className="aq-h2">Try it on your next job</h2>
            <p className="mt-3 text-[hsl(var(--aq-muted))]">5 quote creations in total, shared across Auto, Template and Manual quotes.</p>
            <div className="mt-6 flex justify-center"><TrialBox testId="list-trial-final" /></div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
              <PrimaryCta testId="button-final-signup" />
              <a href={PRICING_URL} className="aq-btn aq-btn-ghost w-full sm:w-auto" data-testid="link-pricing">See pricing</a>
            </div>
          </div>
        </section>

        <section className="aq-section pt-0" data-testid="section-faq" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 id="faq-heading" className="aq-h2 mb-6">Questions</h2>
            <div className="divide-y divide-[hsl(var(--aq-line))] border-y border-[hsl(var(--aq-line))]">
              {FAQS.map((f, i) => (
                <details key={f.q} className="aq-faq group" data-testid={`faq-${i}`}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-bold">
                    {f.q}
                    <Plus className="aq-faq-icon h-5 w-5 shrink-0 text-[hsl(var(--aq-teal))] transition-transform" aria-hidden="true" />
                  </summary>
                  <p className="pb-4 text-[hsl(var(--aq-muted))]">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[hsl(var(--aq-line))] py-8">
        <nav aria-label="Related pages" className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {RELATED_PAGES.map((l) => (
            <Link key={l.href} href={l.href} className="text-[hsl(var(--aq-muted))] hover:text-[hsl(var(--aq-teal-deep))]">{l.label}</Link>
          ))}
        </nav>
      </footer>

      <div className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-[hsl(var(--aq-line))] bg-[hsl(var(--aq-paper)/0.96)] backdrop-blur p-3">
        <Link href="/signup" className="aq-btn aq-btn-primary w-full !min-h-[3rem]" data-testid="button-sticky-signup">
          Start free trial, no card
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
